import {
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { AdminAction } from "../../components/admin";
import {
  detailEntries,
  displayValue,
  formatDate,
  formatLabel,
  formatMoney,
} from "./farmDairyUtils";

function DetailField({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1">
        {label}
      </label>
      <div className="text-zinc-900 break-words">{children}</div>
    </div>
  );
}

function EntryTypeChip({ entryType }) {
  if (!entryType) return "—";
  const income = entryType === "income";
  return (
    <Chip
      size="small"
      label={income ? "Income" : "Expense"}
      color={income ? "success" : "error"}
      variant="outlined"
    />
  );
}

export default function FarmDairyRecordDialog({ record, open, onClose }) {
  if (!record?.data) return null;

  const isActivity = record.kind === "activity";
  const data = record.data;
  const activityDetails = isActivity ? detailEntries(data.details) : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}
      aria-labelledby="farm-dairy-record-title"
    >
      <DialogTitle
        id="farm-dairy-record-title"
        sx={{
          fontWeight: 700,
          pb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        {isActivity ? "Farm activity" : "Crop diary entry"}
        <IconButton size="small" onClick={onClose} aria-label="Close dialog" sx={{ color: "#78716c" }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: "70vh" }}>
        {isActivity ? (
          <div className="grid gap-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <DetailField label="Activity">{data.displayName || "—"}</DetailField>
              <DetailField label="Category">{data.category || "—"}</DetailField>
              <DetailField label="Item">
                {data.subcategory === "Others" ? data.customSubcategory || "Others" : data.subcategory || "—"}
              </DetailField>
              <DetailField label="Created">{formatDate(data.createdAt)}</DetailField>
            </div>
            {activityDetails.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-[#172112] mb-3">Activity details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {activityDetails.map(([key, value]) => (
                    <DetailField key={key} label={formatLabel(key)}>
                      {displayValue(value)}
                    </DetailField>
                  ))}
                </div>
              </div>
            )}
            <div>
              <h3 className="text-sm font-semibold text-[#172112] mb-3">
                Transactions ({data.transactions?.length || 0})
              </h3>
              {data.transactions?.length ? (
                <Table size="small" aria-label="activity transactions">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Transaction</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.transactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>{formatDate(transaction.entryDate)}</TableCell>
                        <TableCell><EntryTypeChip entryType={transaction.entryType} /></TableCell>
                        <TableCell>{transaction.transactionType || "—"}</TableCell>
                        <TableCell>{formatMoney(transaction.amount)}</TableCell>
                        <TableCell>{transaction.notes || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-zinc-500">No transactions recorded for this activity.</p>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-1">
            <DetailField label="Crop">{data.cropName || "—"}</DetailField>
            <DetailField label="Season">{data.season || "—"}</DetailField>
            <DetailField label="Type"><EntryTypeChip entryType={data.entryType} /></DetailField>
            <DetailField label="Category">{data.category || "—"}</DetailField>
            <DetailField label="Amount">{formatMoney(data.amount)}</DetailField>
            <DetailField label="Cultivation date">{formatDate(data.cultivationDate)}</DetailField>
            <DetailField label="Harvesting date">{formatDate(data.harvestingDate)}</DetailField>
            <DetailField label="Entry date">{formatDate(data.entryDate)}</DetailField>
            <DetailField label="Description">{data.description || "—"}</DetailField>
            <DetailField label="Created">{formatDate(data.createdAt)}</DetailField>
          </div>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <AdminAction onClick={onClose} variant="outline">
          Close
        </AdminAction>
      </DialogActions>
    </Dialog>
  );
}
